'use client'

import Image from 'next/image'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  ABHA_RESOLVE_CARD_SHAPE,
  ABHA_RESOLVE_COPY_ICON,
  ABHA_RESOLVE_COPY_SUCCESS_ICON,
  ABHA_RESOLVE_DOWNLOAD_ICON,
  ABHA_RESOLVE_NHA_LOGO,
} from '@components/AbhaResolve/constants'
import {
  ABHA_HERO_CARD_REVEAL_RESIDUE_IMAGE,
  ABHA_HERO_SUCCESS_REVEAL_DURATION_MS,
} from '@components/NewProduct/constants'
import type { AbhaSuccessCardVariant } from '@components/NewProduct/AbhaHeroWidget/steps/abhaSuccessTypes'
import { ABHA_MASKED_NUMBER_DISPLAY, getAbhaProfileInitials, type AbhaCardPreviewData } from '@helpers/abhaWidgetHelper'
import { getClassNames } from '@helpers/productWidgetHelper'
import { useDownload } from '@hooks/useDownload'
import './abhaHeroCard.css'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'

export type AbhaHeroCardLayout = 'default' | 'resolve'

export type AbhaHeroCardProps = {
  data: AbhaCardPreviewData
  className?: string
  variant?: AbhaSuccessCardVariant
  layout?: AbhaHeroCardLayout
  animateReveal?: boolean
  showDownloadAction?: boolean
  showCopyAction?: boolean
  footerShapeUrl?: string
  nhaLogoUrl?: string
  downloadUrl?: string
  onDownload?: () => void
  onCopyAbhaNumber?: () => void
  numberActions?: ReactNode
  topActions?: ReactNode
}

export const AbhaHeroCard = ({
  data,
  className,
  variant = 'hideAbhaNumber',
  layout = 'default',
  animateReveal,
  showDownloadAction = false,
  showCopyAction = false,
  footerShapeUrl = ABHA_RESOLVE_CARD_SHAPE,
  nhaLogoUrl = ABHA_RESOLVE_NHA_LOGO,
  downloadUrl,
  onDownload,
  onCopyAbhaNumber,
  numberActions,
  topActions,
}: AbhaHeroCardProps) => {
  const { download } = useDownload()
  const [isCopied, setIsCopied] = useState(false)
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const isResolveLayout = layout === 'resolve'
  const resolvedVariant = isResolveLayout ? 'showAbhaNumber' : variant
  const shouldAnimateReveal = animateReveal ?? resolvedVariant === 'hideAbhaNumber'
  const displayNumber =
    resolvedVariant === 'showAbhaNumber'
      ? data.abhaNumber ?? data.maskedAbhaNumber
      : data.maskedAbhaNumber ?? ABHA_MASKED_NUMBER_DISPLAY
  const dobLine = data.relationshipLabel ? `${data.dateOfBirth} • ${data.relationshipLabel}` : data.dateOfBirth
  const profileInitials = getAbhaProfileInitials(data.name)

  const revealDurationStyle: any =
    shouldAnimateReveal && !isResolveLayout
      ? { '--abha-card-reveal-duration': `${ABHA_HERO_SUCCESS_REVEAL_DURATION_MS}ms` }
      : undefined

  const handleDownload = () => {
    onDownload?.()

    if (downloadUrl) {
      download(downloadUrl)
    }
  }

  const handleCopy = () => {
    onCopyAbhaNumber?.()
    setIsCopied(true)

    clearTimeout(copyResetTimeoutRef.current)
    copyResetTimeoutRef.current = setTimeout(() => setIsCopied(false), 1500)
  }

  useEffect(
    () => () => {
      clearTimeout(copyResetTimeoutRef.current)
    },
    []
  )

  return (
    <div
      className={getClassNames(['abha-hero-card', `abha-hero-card--${resolvedVariant}`, className ?? ''], {
        'abha-hero-card--resolve': isResolveLayout,
      })}
      style={revealDurationStyle}>
      <div className="abha-hero-card__content">
        <div className="abha-hero-card__header">
          <div className="abha-hero-card__avatar">
            {data.profileImageUrl ? (
              <Image
                src={data.profileImageUrl}
                alt=""
                width={48}
                height={48}
                unoptimized={data.profileImageUrl.startsWith('data:')}
                className="abha-hero-card__avatar-image"
                aria-hidden
              />
            ) : (
              <span className="abha-hero-card__avatar-fallback" aria-hidden>
                {profileInitials}
              </span>
            )}
          </div>

          <div className="abha-hero-card__identity">
            <p className="abha-hero-card__name">{data.name}</p>
            <p className="abha-hero-card__dob">{dobLine}</p>
          </div>

          {topActions}
          <PropertyControlledComponent controllerProperty={isResolveLayout && showDownloadAction} fallback={null}>
            <button
              type="button"
              className="abha-hero-card__download"
              aria-label="Download ABHA card"
              onClick={handleDownload}>
              <Image
                src={ABHA_RESOLVE_DOWNLOAD_ICON}
                alt=""
                width={20}
                height={20}
                className="abha-hero-card__download-icon"
                aria-hidden
              />
            </button>
          </PropertyControlledComponent>
        </div>

        <div className="abha-hero-card__details">
          <div className="abha-hero-card__number-row">
            <p className="abha-hero-card__number">{displayNumber}</p>
            {numberActions}
            <PropertyControlledComponent controllerProperty={isResolveLayout && showCopyAction} fallback={null}>
              <button
                type="button"
                className="abha-hero-card__copy"
                aria-label={isCopied ? 'ABHA number copied' : 'Copy ABHA number'}
                onClick={handleCopy}>
                <Image
                  src={isCopied ? ABHA_RESOLVE_COPY_SUCCESS_ICON : ABHA_RESOLVE_COPY_ICON}
                  alt=""
                  width={18}
                  height={18}
                  className="abha-hero-card__copy-icon"
                  aria-hidden
                />
              </button>
            </PropertyControlledComponent>
          </div>

          <p className="abha-hero-card__address">{data.abhaAddress}</p>
        </div>

        <div className="abha-hero-card__footer">
          <Image src={footerShapeUrl} alt="" width={360} height={78} className="abha-hero-card__shape" aria-hidden />
          <Image
            src={nhaLogoUrl}
            alt="National Health Authority"
            width={120}
            height={36}
            className="abha-hero-card__nha-logo"
          />
        </div>
      </div>

      <PropertyControlledComponent controllerProperty={shouldAnimateReveal} fallback={null}>
        <div className="abha-hero-card__reveal" aria-hidden>
          <div className="abha-hero-card__reveal-cover" />
          <div className="abha-hero-card__reveal-scanner">
            <div className="abha-hero-card__reveal-residue">
              <Image
                src={ABHA_HERO_CARD_REVEAL_RESIDUE_IMAGE}
                alt=""
                width={114}
                height={536}
                className="abha-hero-card__reveal-residue-image"
                aria-hidden
              />
            </div>
            <div className="abha-hero-card__reveal-beam" />
          </div>
        </div>
      </PropertyControlledComponent>
    </div>
  )
}
